import type { ExtractionDataset } from "@/features/data-curation/types";
import { Check, ChevronRight, Copy } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const COPIED_MS = 2_000;

const defaultPrompt = (dataset: Pick<ExtractionDataset, "id" | "name">) =>
  `Use the Paperclip dataset ${dataset.id} ("${dataset.name}"). Read it with paperclip extraction read ${dataset.id}, then help me with the next step.`;

/**
 * A prompt that hands this dataset to the user's own agent (with the Paperclip CLI), collapsed under the
 * methodology of the dataset header.
 * The prompt line is a black terminal. The themes do not remap `neutral`, so
 * it stays black in all of them.
 */
export const AgentPromptBox = ({ dataset }: { dataset: Pick<ExtractionDataset, "id" | "name" | "agent_prompt"> }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), COPIED_MS);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const prompt = dataset.agent_prompt || defaultPrompt(dataset);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
    } catch {
      toast.error("Could not copy the prompt.");
    }
  };

  return (
    <section aria-labelledby="agent-prompt-title" className="mt-2">
      <h3 id="agent-prompt-title" className="text-sm">
        <button
          type="button"
          aria-expanded={open}
          aria-controls="agent-prompt-body"
          onClick={() => setOpen((current) => !current)}
          className="inline-flex items-center gap-1 rounded-[3px] font-medium text-zinc-700 hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900"
        >
          <ChevronRight
            className={`h-4 w-4 motion-safe:transition-transform ${open ? "rotate-90" : ""}`}
            aria-hidden
          />
          Give this dataset to your agent
        </button>
      </h3>
      {open && (
        <div id="agent-prompt-body" className="mt-2">
          <p className="text-sm text-zinc-600">
            Paste this into your agent, such as Claude Code, Cursor or Codex. It gets the whole dataset:
            every row, the quote and source behind each value, and how it was built. Then it can analyze
            it, write it up, or keep adding to it.
          </p>
          <div className="mt-2 overflow-hidden rounded-lg border border-neutral-800 bg-neutral-950 shadow-sm">
            <div className="relative flex items-center border-b border-neutral-800 bg-neutral-900 px-3 py-1.5">
              <span className="flex gap-1.5" aria-hidden>
                <span className="h-2.5 w-2.5 rounded-full bg-red-500" />
                <span className="h-2.5 w-2.5 rounded-full bg-yellow-500" />
                <span className="h-2.5 w-2.5 rounded-full bg-green-500" />
              </span>
              <span className="pointer-events-none absolute inset-x-0 text-center text-xs text-neutral-400">
                Your agent
              </span>
              <button
                type="button"
                onClick={() => void copy()}
                className="relative ml-auto inline-flex items-center gap-1 rounded-[3px] px-1.5 py-0.5 text-xs text-neutral-300 hover:bg-neutral-800 hover:text-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-200"
              >
                {copied ? (
                  <Check className="h-3.5 w-3.5" aria-hidden />
                ) : (
                  <Copy className="h-3.5 w-3.5" aria-hidden />
                )}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <p className="whitespace-pre-wrap break-words px-4 py-3 font-mono text-xs leading-5 text-neutral-50">
              <span className="select-none text-emerald-400" aria-hidden>
                {"~ $ "}
              </span>
              {prompt}
            </p>
          </div>
        </div>
      )}
      <span className="sr-only" aria-live="polite">
        {copied ? "The prompt is copied." : ""}
      </span>
    </section>
  );
};
